import {
  Button,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderTitle,
  PageLoader,
  Tooltip,
  TooltipContent,
  useModals,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useNeedsEmailConfirmation } from '@/features/push-credentials';
import { DocumentTitle } from '@/components/page/document-title';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { SafeNavigate, useSafeNavigate } from '@/services/router/safe-navigate';
import type { App } from '@/services/db/app/entity';
import { useApp, useApps } from '@/services/db/app/queries';
import { AppEditor } from './components/app-editor';
import { TestAppModal } from './components/test-app-modal';
import { useAppActions } from './hooks/use-app-actions';
import { useAppEditor } from './hooks/use-app-editor';
import { useAppsBreadcrumbs, useAppsHrefs } from './hooks/use-apps-breadcrumbs';

const ALREADY_DEFAULT_MESSAGE = 'This app is already your workspace default.';

const EDIT_PERMISSION = 'journeys/push_message_settings/edit';
const DELETE_PERMISSION = 'journeys/push_message_settings/delete';

type EditPageProps = {
  workspaceId: number;
  appId: number;
};

export default function EditPage({ workspaceId, appId }: EditPageProps) {
  const appQuery = useApp(appId);
  const hrefs = useAppsHrefs(workspaceId);
  const app = appQuery.data;

  useAppsBreadcrumbs(
    workspaceId,
    app == null ? undefined : { id: 'workspace_settings_apps_edit', text: app.name, href: hrefs.edit(app.id) },
  );

  // Only a read that leaves nothing to edit sends the user back. A re-read that
  // fails while the app is already on screen keeps the form, and the credentials
  // staged in it, where the user put them.
  if (app == null) {
    if (appQuery.isError) {
      return <SafeNavigate to={hrefs.index} options={{ shouldReplaceHistoryEntry: true }} />;
    }

    return (
      <Page>
        <PageBody>
          <PageLoader />
        </PageBody>
      </Page>
    );
  }

  // Keyed on the app, so opening another one builds a fresh form rather than
  // carrying the last app's staged changes into it.
  return <AppsEditView key={app.id} workspaceId={workspaceId} app={app} indexHref={hrefs.index} />;
}

type AppsEditViewProps = {
  workspaceId: number;
  app: App;
  indexHref: string;
};

function AppsEditView({ workspaceId, app, indexHref }: AppsEditViewProps) {
  const modals = useModals();
  const safeNavigate = useSafeNavigate();
  const { hasPermission } = usePermissions();
  const appsQuery = useApps();
  const isAccountUnconfirmed = useNeedsEmailConfirmation();

  const canEdit = hasPermission(EDIT_PERMISSION) === true;
  const canDelete = hasPermission(DELETE_PERMISSION) === true;

  const goToIndex = () => safeNavigate(indexHref);
  const editor = useAppEditor({
    workspaceId,
    app,
    apps: appsQuery.data ?? [],
    onClose: goToIndex,
    onSaved: goToIndex,
  });

  const actions = useAppActions({
    workspaceId,
    apps: appsQuery.data ?? [],
    onDeleted: () => {
      // The app is gone, so there is nothing left to discard on the way out.
      editor.allowLeave();
      goToIndex();
    },
  });

  // A save applies as a sequence of requests, so promoting or deleting the app
  // mid-flight would interleave with the calls still to come.
  const isBusy = editor.isSaving;

  return (
    <>
      <DocumentTitle title={app.name} />
      <Page>
        <PageHeader>
          <PageHeaderTitle>{app.name}</PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="secondary"
              onClick={() => modals.open(TestAppModal, { workspaceId, apps: [app], isAccountUnconfirmed })}
            >
              Test…
            </Button>
            <Tooltip isCustom={true} isDisabled={!app.is_default}>
              {(context) => (
                <>
                  <Button
                    ref={context.reference.setReference}
                    {...context.reference.getReferenceProps()}
                    variant="secondary"
                    isDisabled={app.is_default || isBusy || !canEdit}
                    onClick={() => void actions.requestSetDefault(app)}
                  >
                    Set as default…
                  </Button>
                  <TooltipContent>{ALREADY_DEFAULT_MESSAGE}</TooltipContent>
                </>
              )}
            </Tooltip>
            <ViewerRestrictedTooltip readOnly={!canDelete}>
              {(context) => (
                <Button
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  variant="secondary"
                  isCritical={true}
                  isLoading={actions.isDeleting}
                  isDisabled={isBusy || actions.isDeleting || !canDelete}
                  onClick={() => void actions.requestDelete(app)}
                >
                  Delete…
                </Button>
              )}
            </ViewerRestrictedTooltip>
          </PageHeaderActions>
        </PageHeader>
        <PageBody>
          <AppEditor editor={editor} />
        </PageBody>
      </Page>
    </>
  );
}
